import type { QueryInteropSupportedArgs } from '@confluence/apollo-relay-interop/entry-points/query.ts';

import { getEnv } from '@confluence/ssr-utilities/entry-points/env.ts';
import {
	getEquivalentQueryNames,
	getQueryName,
} from '@confluence/query-preloader-tools/entry-points/getQueryName.ts';

import { generatedAt, Q, R } from './preloadQueryBatchConfig.ts';

export const preloadQueryBatchConfigGeneratedAt: string | null = generatedAt;

const _cache = new Map<string, ReadonlySet<string>>();
const _empty: ReadonlySet<string> = Object.freeze(new Set<string>());
const _aggPrefix = 'agg_';

function _decode(region: string): ReadonlySet<string> {
	const cached = _cache.get(region);
	if (cached) return cached;
	const hex = R[region];
	if (!hex) return _empty;
	const set = new Set<string>();
	for (let i = 0; i < Q.length; i++) {
		// eslint-disable-next-line no-bitwise
		const byteIdx = i >> 3;
		// eslint-disable-next-line no-bitwise
		const bitIdx = i & 7;
		const byte = parseInt(hex.slice(byteIdx * 2, byteIdx * 2 + 2), 16);
		// eslint-disable-next-line no-bitwise
		if (byte & (1 << bitIdx)) set.add(Q[i]);
	}
	const frozen = Object.freeze(set) as ReadonlySet<string>;
	_cache.set(region, frozen);
	return frozen;
}

function _getEquivalentQueryName(queryName: string): string {
	return queryName.startsWith(_aggPrefix)
		? queryName.slice(_aggPrefix.length)
		: `${_aggPrefix}${queryName}`;
}

function _matchesFastQuery(fastQueries: ReadonlySet<string>, queryName: string): boolean {
	return fastQueries.has(queryName) || fastQueries.has(_getEquivalentQueryName(queryName));
}

/**
 * Check whether a preload query is classified as fast (batchable) for the
 * current SSR region.
 *
 * @param queryNameOrOperationName — the GraphQL operation name or serialized query key.
 */
export function isFastPreloadQuery<V = QueryInteropSupportedArgs<any, any>['variables']>(
	queryNameOrOperationName: string,
	variables?: V,
): boolean {
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	const region = process.env.REACT_SSR ? getEnv() : window.__SSR_REGION__;
	if (!region || !R[region]) return false;
	const fastQueries = _decode(region);

	if (variables === undefined) {
		return _matchesFastQuery(fastQueries, queryNameOrOperationName);
	}

	const queryName = getQueryName(queryNameOrOperationName, variables, true);
	if (_matchesFastQuery(fastQueries, queryName)) {
		return true;
	}

	const equivalentQueryNames = getEquivalentQueryNames(queryNameOrOperationName, variables, true);
	return (
		equivalentQueryNames.length > 1 &&
		equivalentQueryNames.every((equivalentQueryName) =>
			_matchesFastQuery(fastQueries, equivalentQueryName),
		)
	);
}
